import React from 'react';
import DOMPurify from 'dompurify';
import { Calendar, User } from 'lucide-react';
import type { RmContentVideo } from '../../types/rmContent';
import { buildVimeoEmbedUrl } from '../../utils/urlUtils';
import RmContentFooter from './RmContentFooter';

interface RmVideoViewerProps {
  content: RmContentVideo;
}

const formatDate = (dateStr: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(dateStr));
  } catch {
    return '';
  }
};

const RmVideoViewer: React.FC<RmVideoViewerProps> = ({ content }) => {
  const teacherNames = content.teacher
    ?.map((t: any) => (typeof t === 'string' ? t : t.title || t.slug || ''))
    .filter(Boolean);

  return (
    <div className="flex flex-col">
      {/* Video player — theater mode */}
      {content.vimeoId && (
        <div className="bg-gray-950 px-4 py-4 sm:px-6 sm:py-6 flex items-center justify-center">
          <div className="w-full max-w-[90%] max-h-[55vh] mx-auto rounded-xl overflow-hidden shadow-2xl aspect-video">
            <iframe
              src={buildVimeoEmbedUrl(content.vimeoId)}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              referrerPolicy="strict-origin-when-cross-origin"
              title={content.title || 'Video'}
              loading="lazy"
            />
          </div>
        </div>
      )}

      {/* Info section */}
      <div className="max-w-3xl mx-auto px-6 pt-6">
        {content.subtitle && (
          <p className="text-lg font-medium text-rm-ink-900 leading-relaxed">{content.subtitle}</p>
        )}

        {(content.publishedAt || teacherNames?.length > 0) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-3">
            {content.publishedAt && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(content.publishedAt)}
              </span>
            )}
            {teacherNames?.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                {teacherNames.join(', ')}
              </span>
            )}
          </div>
        )}

        {content.description && (
          <>
            <div className="w-16 h-0.5 bg-rm-accent-500 rounded-full my-5" />
            <div
              className="prose prose-base max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.description) }}
            />
          </>
        )}

        <RmContentFooter pillar={content.pillar} teacher={content.teacher} tags={content.tags} />
      </div>
    </div>
  );
};

export default RmVideoViewer;
